import {SessionInformation} from '@mintdata-wsp/common/controllers/network/extended/responses/SessionInformation';
import {WspAPI} from '@mintdata-wsp/common/controllers/network/extended/WspAPI';
import {EventBus, HasEvents} from '@mintdata-wsp/common/bus/EventBus';
import {EventHandler, RestEngineSocketClient} from '@mintdata-wsp/common/controllers/network/socket/RestEngineSocketClient';
import {SocketConfig} from '@mintdata-wsp/common/controllers/network/socket/SocketConfig';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {IJsonPatch} from '@mintdata-wsp/thirdparty/jiff/lib/jsonPatch';
import {Category, Logger} from '@mintdata/mint-kit/api/log/mk';
import bind from 'bind-decorator';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {rootState} from '@mintdata-wsp/common/state/RootState';

const logger = Logger.getLogger(Category.Multiplayer);

export type InitEvent = {
    designName:string;
    uuid:string;
    currentDesign:any;
    currentVersion:number;
    lastSyncValueEventsBySlotId:{
        [key:string]:SyncValueEvent
    }
}

export type SyncDesignEvent = {
    ackedVersion:number;
    clientTimestamp:number;
    serverTimestamp:number;
    edits:Array<DesignEdit>;
}

export type DesignEdit = {
    baseVersion:number
    patches:Array<IJsonPatch>
}

export type SyncValueEvent = {
    sheetId:string,
    slotId:string,
    value:any
}

export type ActiveUserEvent = {
    userId:number;
    cursorAt?:string;   // possible values: "surface", "spreadsheet" or "nestedframe"
    mouseX?:number;     // relative to surface / spreadsheet / etc
    mouseY?:number;     // relative to surface / spreadsheet / etc
    page?:string;       // UUID of active page
    components?:string[];  // UUIDs of selected components
    sheet?:string;      // UUID of active sheet
    range?:string;      // 1_1:5_5 (col_row)
    timestamp:number;   // timestamp of the event
    // follow me data
    spreadsheetPanelWidth?:number;    // 0 = hidden, 100 = fullscreen, everything else is percentage
    surfacePanX?:number;
    surfacePanY?:number;
    surfaceZoom?:number;
    sheetScrollX?:number;
    sheetScrollY?:number;
    boxMoveState?:any;   // user moves a box (a group of boxes) or resizes a box
}

export type ActiveUsersEvent = {
    users:ActiveUserEvent[]
}

export type SnapshotRestoredEvent = {
    currentDesign:any;
    currentVersion:number;
}

export type DesignReloadedEvent = {
    projectId:number;
    designId:number;
}

export type MultiplayerLifecycleListener = {
    onConnected?():void
    onDisconnected?():void

    onConnectionLost?():void
    onConnectionRestored?():void
}

type EventToTrigger = {
    eventName:string;
    event:any;
    handler:EventHandler;
}

export class MultiplayerController implements HasEvents<MultiplayerLifecycleListener>
{
    private projectId:number;
    private designId:number;
    private userId:number;

    private reconnectionTimerId:any;
    private connected:boolean = false;

    private socketClient:RestEngineSocketClient;

    private mpWspApiEndpoint:WspAPI;

    private initEventReceived:boolean = false;
    private eventsToTriggerAfterInit:Array<EventToTrigger> = [];

    private connectionError:string = null;

    public async connect(projectId:number, designId:number, userId:number, isBustCache:boolean):Promise<void>
    {
        this.disconnect();

        this.projectId = projectId;
        this.designId = designId;
        this.userId = userId;

        return this.tryConnect().then(() =>
        {
            logger.debug('MP :: Initial MP connect succeded');

            if( isBustCache ) {
                this.sendEvent('reload-design');
                // clear hash data
                history.pushState("", document.title, window.location.pathname + window.location.search);
            }

            this.connected = true;
            EventBus.fire(this, 'connected');
        })
        .catch(e =>
        {
            if( !this.connectionError )
                this._tryReconnect();

            throw(e);
        });
    }

    public disconnect()
    {
        if( !this.connected )
            return;

        this.connected = false;

        this._disconnectSocketClient();
        this._clearReconnectionTimer();

        EventBus.fire(this, 'disconnected');
    }

    private _disconnectSocketClient()
    {
        if( this.socketClient )
        {
            this.socketClient.disconnect();
            this.socketClient = null;
            this.mpWspApiEndpoint = null;
            this.initEventReceived = false;
            this.eventsToTriggerAfterInit = [];
        }
    }

    private _clearReconnectionTimer()
    {
        if( this.reconnectionTimerId )
        {
            clearInterval(this.reconnectionTimerId);
            this.reconnectionTimerId = null;
        }
    }

    public async reconnect()
    {
        await this.connect(this.projectId, this.designId, this.userId, false);
    }

    public sendEvent(eventName:string, body:any = null):Promise<void>
    {
        if( !this.socketClient || !this.socketClient.isConnected() )
        {
            logger.warn(`Trying to send event ${eventName}, when disconnected: `, body);
            return Promise.reject(null);
        }

        return this.socketClient.send(eventName, body);
    }

    //TODO: it's annoying that now we need to subscribe on both connect and connectionRestored...
    // should probably have a collection of listeners inside this controller itself and auto-re-subscribe on reconnect
    public subscribeToEvents(eventName:string, handler:EventHandler)
    {
        if( !this.socketClient )
        {
            logger.warn(`Trying to subscribe to events ${eventName}, when disconnected`);
            return;
        }

        this.socketClient.subscribe(eventName, (event) =>
        {
            // store incoming events until we get initEvent through REST
            if( this.initEventReceived )
                handler(event);
            else
                this.eventsToTriggerAfterInit.push({eventName, event, handler});
        });
    }

    @bind
    private _tryReconnect()
    {
        // if( !this.connected || this.reconnectionTimerId )
        //     return;

        EventBus.fire(this, 'connectionLost');

        this._disconnectSocketClient();

        this._scheduleReconnectionTimer();
    }

    @bind
    private _scheduleReconnectionTimer(millis:number = 0)
    {
        this.reconnectionTimerId = setTimeout(() =>
        {
            this.tryConnect()
                .then(() => this._onReconnectionSuccess())
                .catch(() => this._scheduleReconnectionTimer(1000));
        }, millis);
    }

    @bind
    private _onReconnectionSuccess()
    {
        this._clearReconnectionTimer();

        this.retrieveInitEvent();

        EventBus.fire(this, 'connectionRestored');
    }

    private async tryConnect():Promise<void>
    {
        const {projectId, designId} = this;
        const resolution = await API.multiplayer.resolve(projectId, designId);

        if( resolution instanceof ApiError && resolution.code === 400 )
        {
            this.connectionError = resolution.message;
            throw new Error(resolution.message);
        }
        else
            this.connectionError = null;

        if( !resolution || !resolution.connectHostname)
            throw new Error('Cannot connect to the cloud. Please check your network connection first. If everything is fine on your side, please contact MintData support.');

        if( !this.userId )
            this.userId = await controllers.user.syncUserId();

        if( !this.userId )
            throw new Error('Failed to get userId');

        const socketClient =
            this.initializeSocketClient(resolution.connectHostname, projectId, designId, this.userId);

        await socketClient.connect();

        const apiProtocol = ConfigUtil.apiSecure() ? 'https' : 'http';
        const apiUrl = `${apiProtocol}://${resolution.connectHostname}`;
        this.mpWspApiEndpoint = API.overrideBaseUrl(apiUrl);
        rootState.net.multiplayerInstanceApiEndpoint = this.mpWspApiEndpoint;
        this.socketClient = socketClient;
        this.socketClient.addSocketListener({
            onClose: this._tryReconnect
        });
    }

    private initializeSocketClient(connectHostname:string, projectId:number, designId:number, userId:number):RestEngineSocketClient
    {
        const apiSecure = ConfigUtil.apiSecure();
        const apiPort = apiSecure ? 443 : 80;
        const wsSyncUrl = `/api/ws/multiplayer?projectId=${projectId}&designId=${designId}&userId=${userId}`;

        const socketConfig = new SocketConfig(apiSecure, connectHostname, apiPort, wsSyncUrl);
        return new RestEngineSocketClient(socketConfig, 1, false);
    }

    public async retrieveInitEvent()
    {
        const sesssionInformation:SessionInformation =
            await this.socketClient.request('session-info', SessionInformation);
        const sessionId = sesssionInformation.sessionId;

        const initEvent = await this.mpWspApiEndpoint.multiplayer
            .getInitEvent(this.projectId, this.designId, sessionId);

        for( let {eventName, event, handler} of this.eventsToTriggerAfterInit )
        {
            handler(
                eventName === 'init' ? initEvent : event
            );
        }

        this.eventsToTriggerAfterInit = [];
        this.initEventReceived = true;
    }
}
